import {
  Box,
  IconButton,
  Modal,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import WorkItem from "../../Components/WorkItem";
import CloseIcon from "@mui/icons-material/Close";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import { motion } from "framer-motion";

const Pintly1 = require("../../assets/pintlyImg.jpg");
const node4 = require("../../assets/node4Logo.png");
const logo = require("../../assets/download.jpeg");
const blobA = require("../../assets/magicpattern-blob-1710781780794.png");
const blobB = require("../../assets/magicpattern-blob-1710787651582.png");

interface WorkModalState {
  open: boolean;
  title?: string;
  images?: string[];
  bannerLabel?: string;
  bannerGradient?: string;
  role?: string;
  date?: string;
  techStack?: string[];
  longSummary?: string;
  links?: { label: string; value: string };
}

interface WorkItemData {
  title: string;
  images?: string[];
  bannerLabel?: string;
  bannerGradient?: string;
  role: string;
  techStack: string[];
  links: { label: string; value: string };
  date: string;
  longSummary: string;
}

const workItems: WorkItemData[] = [
  {
    title: "Equals Money",
    bannerLabel: "Equals Money",
    bannerGradient: "linear-gradient(135deg, #4338ca 0%, #1e1b4b 100%)",
    role: "Software Engineer",
    techStack: ["TypeScript", "React", "Next.js", "Node.js"],
    links: { label: "Equals Money website", value: "https://www.equalsmoney.com/" },
    date: "Nov 2024-Present",
    longSummary: `Full-time · London Area, United Kingdom · Hybrid

Contributed to the development of the Cards domain within Equals Money's fintech platform. Built and maintained customer-facing features for physical and virtual cards, card ordering and management, Apple Pay and Google Pay wallet integration, and integrations with third-party card service providers.

Primarily focused on frontend engineering while contributing across the full stack, collaborating with product managers, designers, backend engineers, and QA to deliver secure, scalable, and high-performance financial products in a regulated environment.`,
  },
  {
    title: "Pintly",
    images: [Pintly1],
    role: "Founder/Developer",
    techStack: ["TypeScript", "React Native", "Redux"],
    links: {
      label: "Download the app here!",
      value: "https://linktr.ee/pintly",
    },
    date: "2024-Present",
    longSummary: `I co-founded and built the entire front end of Pintly. An app that shows pint prices for pubs in London.
Since launch the app has gained 60k+ users, reaching the top of the UK food and drinks category.`,
  },
  {
    title: "345 Global",
    images: [logo],
    role: "Front End Engineer",
    techStack: ["TypeScript", "React", "Redux", "MUI"],
    links: { label: "345 Global Website", value: "https://www.345.global/" },
    date: "2021-2024",
    longSummary: `In the two and a half years I spent at 345 Global, my role involved building complex features that were vital for enhancing the app's user experience, including developing a secure login mechanism, an interactive comment system, and responsive notifications. I was responsible and held accountable for a large portion of the application's front end.`,
  },
  {
    title: "Node4",
    images: [node4],
    role: "Full Stack Software Engineer",
    techStack: ["TypeScript", "React", "Redux", "Python"],
    links: { label: "Node4 website", value: "https://node4.co.uk/" },
    date: "2020-2021",
    longSummary: `As a Full Stack Engineer at Node4, I gained valuable experience working on both front-end and back-end components of web applications. I was involved in various projects and contributed to the development of new features and functionalities.
• Primarily used TypeScript and Python.
• Collaborated with engineers and designers to implement features for web applications.
• Participated in debugging, testing, and maintaining the codebase for optimal performance.`,
  },
];

const Work = ({ onToggle }: any) => {
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState<WorkModalState>({ open: false });

  const handleOpenModal = (values: Omit<WorkModalState, "open">) => {
    setModalOpen({ open: true, ...values });
  };
  const handleCloseModal = () => setModalOpen({ open: false });

  const isMobile = useMediaQuery(theme.breakpoints.down(1000));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down(800));

  return (
    <Box
      id="work"
      sx={{
        position: "relative",
        width: "100%",
        minHeight: "var(--app-height, 100svh)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        overflow: "hidden",
        background: `
          linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 42%, #f4f7fb 70%, #eef2f9 100%),
          linear-gradient(90deg, #e3ffe7 0%, #d9e7ff 100%)
        `,
        paddingTop: "clamp(64px, 10vh, 100px)",
        paddingBottom: "clamp(72px, 12vh, 120px)",
      }}
    >
      {/* decorative blobs, matching the hero for a seamless feel.
          Kept inside the section's own overflow:hidden bounds (small negative
          offsets only) so the mask fade — not the clip edge — is what's ever visible. */}
      <Box
        component="img"
        src={blobA}
        alt=""
        aria-hidden="true"
        sx={{
          position: "absolute",
          top: "-40px",
          left: "-60px",
          width: "clamp(260px, 30vw, 460px)",
          height: "clamp(260px, 30vw, 460px)",
          filter: "blur(8px)",
          maskImage: "radial-gradient(circle, #000 15%, transparent 50%)",
          WebkitMaskImage:
            "radial-gradient(circle, #000 15%, transparent 50%)",
          opacity: 0.7,
          zIndex: 0,
          pointerEvents: "none",
          animation: "floatSideways 9s ease-in-out infinite",
        }}
      />
      <Box
        component="img"
        src={blobB}
        alt=""
        aria-hidden="true"
        sx={{
          position: "absolute",
          bottom: "-40px",
          right: "-50px",
          width: "clamp(240px, 28vw, 440px)",
          height: "clamp(240px, 28vw, 440px)",
          filter: "blur(8px)",
          maskImage: "radial-gradient(circle, #000 15%, transparent 50%)",
          WebkitMaskImage:
            "radial-gradient(circle, #000 15%, transparent 50%)",
          opacity: 0.6,
          zIndex: 0,
          pointerEvents: "none",
          animation: "float 8s ease-in-out infinite",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          maxWidth: "1260px",
          margin: "0 auto",
          padding: "0 clamp(20px, 5vw, 40px)",
          [theme.breakpoints.down(600)]: {
            padding: "0 32px",
          },
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: "10px",
            mb: "56px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "inter",
              fontSize: "clamp(28px, 4.5vw, 40px)",
              fontWeight: 700,
              color: "#111111",
            }}
          >
            my work.
          </Typography>
          <Typography
            sx={{
              fontFamily: "inter",
              fontSize: "16px",
              fontWeight: 500,
              color: "rgba(0, 0, 0, 0.5)",
              maxWidth: "440px",
            }}
          >
            A few products I've worked on, click a card for more details.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "clamp(28px, 4vw, 48px)",
            width: "100%",
          }}
        >
          {workItems.map((item) => (
            <WorkItem
              key={item.title}
              title={item.title}
              images={item.images}
              bannerLabel={item.bannerLabel}
              bannerGradient={item.bannerGradient}
              role={item.role}
              date={item.date}
              techStack={item.techStack}
              longSummary={item.longSummary}
              links={item.links}
              onModalOpen={handleOpenModal}
            />
          ))}
        </Box>
      </Box>

      <Modal
        open={modalOpen.open}
        onClose={handleCloseModal}
        closeAfterTransition
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, scale: 0.94, y: 16 }}
          animate={{
            opacity: modalOpen.open ? 1 : 0,
            scale: modalOpen.open ? 1 : 0.94,
            y: modalOpen.open ? 0 : 16,
          }}
          transition={{ duration: 0.28, ease: "easeOut" }}
          sx={{
            width: isSmallMobile ? "100%" : isMobile ? "85%" : "560px",
            maxHeight: "85vh",
            overflowY: "auto",
            position: "relative",
            outline: "none",
            borderRadius: "28px",
            background: "rgba(255, 255, 255, 0.68)",
            backdropFilter: "blur(34px) saturate(180%)",
            WebkitBackdropFilter: "blur(34px) saturate(180%)",
            boxShadow:
              "0 8px 32px rgba(31, 38, 135, 0.14), 0 40px 90px rgba(0, 0, 0, 0.3)",
          }}
        >
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              top: "16px",
              right: "16px",
              zIndex: 2,
              width: "34px",
              height: "34px",
              background: "rgba(255, 255, 255, 0.7)",
              backdropFilter: "blur(10px)",
              "&:hover": { background: "rgba(255, 255, 255, 0.9)" },
            }}
          >
            <CloseIcon sx={{ fontSize: "18px", color: "#111111" }} />
          </IconButton>

          <Box
            sx={{
              width: "100%",
              height: "200px",
              overflow: "hidden",
              borderRadius: "28px 28px 0 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                modalOpen.bannerGradient ??
                "linear-gradient(135deg, #1f2937 0%, #0b0f19 100%)",
            }}
          >
            {modalOpen.images && modalOpen.images.length > 0 ? (
              <Box
                component="img"
                src={modalOpen.images[0]}
                alt={`${modalOpen.title} preview`}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: modalOpen.bannerGradient,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "inter",
                    fontSize: "28px",
                    fontWeight: 700,
                    letterSpacing: "0.5px",
                    color: "#ffffff",
                  }}
                >
                  {modalOpen.bannerLabel ?? modalOpen.title}
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ padding: "32px" }}>
            <Typography
              sx={{
                fontFamily: "inter",
                fontSize: "26px",
                fontWeight: 700,
                color: "#111111",
                mb: "4px",
              }}
            >
              {modalOpen.title}
            </Typography>
            <Typography
              sx={{
                fontFamily: "inter",
                fontSize: "15px",
                fontWeight: 500,
                color: "rgba(0, 0, 0, 0.5)",
                mb: "24px",
              }}
            >
              {modalOpen.role} · {modalOpen.date}
            </Typography>

            <Typography
              sx={{
                fontFamily: "inter",
                fontSize: "15px",
                fontWeight: 400,
                lineHeight: 1.65,
                color: "rgba(0, 0, 0, 0.72)",
                whiteSpace: "pre-line",
                mb: "28px",
              }}
            >
              {modalOpen.longSummary}
            </Typography>

            <Typography
              sx={{
                fontFamily: "inter",
                fontSize: "13px",
                fontWeight: 700,
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                color: "rgba(0, 0, 0, 0.4)",
                mb: "10px",
              }}
            >
              Tech stack
            </Typography>
            <Box
              sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mb: "32px" }}
            >
              {modalOpen.techStack?.map((tech: string) => (
                <Box
                  key={tech}
                  sx={{
                    fontFamily: "inter",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "rgba(0, 0, 0, 0.65)",
                    background: "rgba(0, 0, 0, 0.055)",
                    borderRadius: "999px",
                    padding: "5px 12px",
                  }}
                >
                  {tech}
                </Box>
              ))}
            </Box>

            {modalOpen.links?.value && (
              <Box
                component="a"
                target="_blank"
                rel="noreferrer"
                href={modalOpen.links.value}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  textDecoration: "none",
                  fontFamily: "inter",
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "#ffffff",
                  background: "#111111",
                  borderRadius: "999px",
                  padding: "12px 22px",
                  transition: "opacity 0.2s ease",
                  "&:hover": { opacity: 0.82 },
                }}
              >
                {modalOpen.links.label}
                <NorthEastIcon sx={{ fontSize: "16px" }} />
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default Work;
